import classNames from 'classnames'
import { parseJson } from 'legacy/lut/classes/helpers/JsonHelper'
import { IELTData, IELTDataSettings } from 'legacy/lut/classes/models/partner360/ELT/IELTData'
import { getEltDocFormatTranslation } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getEltDocFormatTranslation'
import { TSelectVersionMode } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/versions/types/Types'
import { downloadFileByUrl } from 'legacy/lut/helpers/files/downloadFileByUrl'
import { useCallback } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import { IColumn } from 'core-components/library/list/types'
import { Tooltip } from 'core-components/library/tooltip'
import { getFormatDate } from 'core/classes/helpers/DateHelper'
import { calculateFileLink } from 'core/classes/helpers/saveFiles'

import { getFormedEltTranslation } from '../../../helpers/getFormedEltTranslation'
import { getPreviousQuarterAndYear } from '../../../helpers/getPreviousYearAndQuarter'
import { EltPeriodCell } from '../../styledComponents/periodCell/EltPeriodCell'
import { getVersionCrud } from '../helpers/getVersionCrud'
import styles from '../versionsHandler/VersionsHandler.module.scss'

interface IGetColumns {
  selectVersion: (
    mode: TSelectVersionMode
  ) => (version: IELTData, versionName?: string, versionSerialNumber?: number) => void
  setSelectedVersionToClone: (version: IELTData) => void
  setItemToUpdate: (version: IELTData) => void
  setItemToDelete: (version: IELTData) => void
}
const getColumns = ({
  selectVersion,
  setItemToDelete,
  setItemToUpdate,
}: IGetColumns): IColumn<IELTData>[] => [
  { title: getFormedEltTranslation('versions-version'), field: 'Version' },
  { title: getFormedEltTranslation('versions-version-name'), field: 'VersionName' },
  {
    title: 'jbp-financial-cockpit-period-label',
    field: 'FYear',
    renderFunction: (item) => {
      if (!item.FYear || !Number.isInteger(item.FQuarter) || item.FQuarter < 1 || item.FQuarter > 4)
        return <span>—</span>
      const { year, quarter } = getPreviousQuarterAndYear(item.FYear, item.FQuarter)
      return <EltPeriodCell fYear={year} fQuarter={quarter} />
    },
  },
  {
    title: getFormedEltTranslation('versions-elt-type'),
    field: 'EltType',
    renderFunction: (item) => {
      return <Translation text={getEltDocFormatTranslation(item.EltType)} />
    },
  },
  {
    title: getFormedEltTranslation('versions-net-ndp'),
    field: 'Settings',
    renderFunction: (item) => {
      const settings = parseJson<IELTDataSettings>(item.Settings)
      return <div>{settings?.NetNdp || '-'}</div>
    },
  },
  {
    title: getFormedEltTranslation('versions-form-modified'),
    field: 'FormModified',
    renderFunction: (item) => {
      return <div>{getFormatDate(item.FormModified, 'full')}</div>
    },
  },
  { title: getFormedEltTranslation('versions-form-modified-by'), field: 'FormModifiedBy' },
  {
    title: '',
    field: 'Icons',
    hideSettings: true,
    renderFunction: (item) => (
      <Flex>
        <Tooltip
          tipContent={() => {
            return (
              <Translation
                text={getFormedEltTranslation(
                  item.PPTXReport ? 'download-exported-doc' : 'document-not-generated'
                )}
              />
            )
          }}
        >
          <Button
            variant={'text'}
            onClick={() => {
              const link = calculateFileLink({
                formId: 1233,
                fieldName: 'PPTXReport',
                UID: item.UID,
                fileName: item.PPTXReport,
                entryId: item.EntryId,
              })
              downloadFileByUrl(link, 'elt-form')
            }}
            iconButton={'kbase kbase-download'}
            className={classNames(styles.icons__icon)}
            disabled={!item.PPTXReport}
          />
        </Tooltip>
        <Tooltip
          tipContent={() => <Translation text={getFormedEltTranslation('change-version')} />}
        >
          <Button
            variant={'text'}
            onClick={() => selectVersion('change')(item)}
            iconButton={'kbase kbase-article'}
            className={classNames(styles.icons__icon)}
          />
        </Tooltip>
        <Tooltip
          tipContent={() => <Translation text={getFormedEltTranslation('update-version-name')} />}
        >
          <Button
            variant={'text'}
            onClick={() => setItemToUpdate(item)}
            iconButton={'kbase kbase-edit'}
            className={classNames(styles.icons__icon)}
          />
        </Tooltip>
        <Tooltip
          tipContent={() => <Translation text={getFormedEltTranslation('delete-version')} />}
        >
          <Button
            variant={'text'}
            onClick={() => setItemToDelete(item)}
            iconButton={'kbase kbase-delete'}
            className={classNames(styles.icons__icon)}
          />
        </Tooltip>
      </Flex>
    ),
  },
]

export const useVersionsTableData = () => {
  const crud = getVersionCrud()

  const refetch = useCallback(() => {
    crud.removeQueries(true)
  }, [crud])

  return { versionsSource: crud, getColumns, refetch }
}
